import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/auth-context'
import {
  clearAuthSession,
  completePasswordReset,
  requestPasswordReset,
} from '../services/auth-service'
import { PasswordField } from '../components/password-field'
import { BRAND_LOGO_URL } from '../utils/brand-logo'
import { DEVELOPER_CONTACT_EMAIL } from '../utils/contact-email'
import { consumeSessionExpiredReason } from '../utils/session-tracker'
import { sessionExpiredMessage } from '../config/session-policy'
import {
  authErrorMessage,
  isForceChangePasswordResetError,
  isLimitExceededError,
  PASSWORD_RESET_COOLDOWN_MS,
} from '../utils/auth-error-message'
import {
  readTotpSetupFromSignIn,
  type TotpEnrolmentSecret,
} from '../services/totp-mfa-service'
import { TotpEnrolmentPanel } from '../features/security/components/totp-enrolment-panel'
import { isCompleteTotpCode } from '../features/security/totp-code'
import { totpChallengeErrorMessage } from '../features/security/totp-errors'
import { recordWebMfaUnlock } from '../stores/app-lock-store'
import { isMobileWeb, isNativeApp } from '../utils/platform'

type Step =
  | 'email'
  | 'password'
  | 'new-password'
  | 'forgot-username'
  | 'reset-code'
  | 'totp-code'
  | 'totp-setup'

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

/**
 * The web weekly MFA re-verifies password + an emailed one-time secret. When a
 * flow in THIS session already proved both factors — the invite flow (temp
 * password arrived by email, then a new password was set) or a password reset
 * (emailed code + new password) — start the 7-day clock now instead of
 * soft-locking the brand-new session with a second "Verify it's you" prompt
 * seconds after sign-in (which read as a freeze on iOS Safari invites).
 */
function satisfyWebMfaFromEmailedSecret(): void {
  if (isNativeApp()) return
  recordWebMfaUnlock()
}

/**
 * Invite links open in the phone's browser, which confused testers about
 * whether to use the app or the web. Shown on the sign-in landing and the
 * invite set-password step for mobile-web visitors only.
 */
function MobileAppHint() {
  if (isNativeApp() || !isMobileWeb()) return null
  return (
    <div
      role="note"
      aria-label="Using the mobile app?"
      className="mb-4 flex items-start gap-2.5 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800"
    >
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        className="mt-0.5 h-4 w-4 flex-none"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="7" y="2" width="10" height="20" rx="2" />
        <path d="M11 18h2" />
      </svg>
      <p>
        <span className="font-semibold">Using the mobile app?</span> Open{' '}
        <span className="font-semibold">Ascend Messenger</span> on your phone and sign in there
        with these same details. This page is the web version — both work with one account.
      </p>
    </div>
  )
}

export function SignInPage() {
  const {
    signInEmail,
    confirmNewPassword: submitNewPasswordChallenge,
    confirmTotpCode,
    isConfigured,
    user,
    isLoading,
  } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [totpSetup, setTotpSetup] = useState<TotpEnrolmentSecret | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [resetCooldownUntil, setResetCooldownUntil] = useState(0)
  const [cooldownTick, setCooldownTick] = useState(0)
  const resetInFlight = useRef(false)

  useEffect(() => {
    const reason = consumeSessionExpiredReason()
    if (reason) setMessage(sessionExpiredMessage(reason))
  }, [])

  // Tick once per second while a ForgotPassword cooldown is active.
  useEffect(() => {
    if (resetCooldownUntil <= Date.now()) return
    const id = window.setInterval(() => setCooldownTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [resetCooldownUntil])

  const trimmedEmail = email.trim()
  const emailValid = isValidEmail(trimmedEmail)
  const resetCooldownRemainingSec = Math.max(
    0,
    Math.ceil((resetCooldownUntil - Date.now()) / 1000),
  )
  // Reference cooldownTick so the remaining seconds re-render each tick.
  void cooldownTick

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/messages/direct', { replace: true })
    }
  }, [user, isLoading, navigate])

  const resetFeedback = () => {
    setError(null)
    setMessage(null)
  }

  const startResetCooldown = () => {
    setResetCooldownUntil(Date.now() + PASSWORD_RESET_COOLDOWN_MS)
  }

  const handleContinueToPassword = (e: React.FormEvent) => {
    e.preventDefault()
    resetFeedback()
    if (!emailValid) {
      setError('Enter a valid email address')
      return
    }
    setStep('password')
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    resetFeedback()
    if (!password) {
      setError('Enter your password')
      return
    }
    setSubmitting(true)
    try {
      const result = await signInEmail(trimmedEmail, password)
      if (result.isSignedIn) {
        navigate('/messages/direct', { replace: true })
        return
      }
      const nextStep = result.nextStep?.signInStep
      if (nextStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        // Account has an authenticator app registered. Accounts without one
        // never reach here — the pool is MFA=OPTIONAL.
        setTotpCode('')
        setStep('totp-code')
        return
      }
      if (nextStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        // Only reachable once the pool is flipped to MFA=REQUIRED: the
        // password was correct but no authenticator exists yet.
        setTotpSetup(readTotpSetupFromSignIn(result, trimmedEmail))
        setMessage(
          'Your password was accepted. Set up an authenticator app to finish signing in.',
        )
        setStep('totp-setup')
        return
      }
      if (nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Invited users (FORCE_CHANGE_PASSWORD) must set a permanent password.
        setNewPassword('')
        setConfirmNewPassword('')
        setMessage('Your temporary password worked. Set a new password to finish signing in.')
        setStep('new-password')
        return
      }
      if (nextStep === 'RESET_PASSWORD') {
        // An admin reset this account (Cognito RESET_REQUIRED). Email a
        // verification code and send the user straight to the reset-code step
        // so they can set a new password themselves — no support email needed.
        // Respect cooldown so we do not burn Cognito ForgotPassword quota.
        if (resetCooldownRemainingSec > 0) {
          setError(
            `A reset email was recently requested. Wait ${resetCooldownRemainingSec}s before requesting another code, or enter a code you already received.`,
          )
          setStep('reset-code')
          return
        }
        try {
          await requestPasswordReset(trimmedEmail)
          startResetCooldown()
          setResetCode('')
          setNewPassword('')
          setConfirmNewPassword('')
          setMessage(
            `Your password needs to be reset. We've emailed a verification code to ${trimmedEmail} — ` +
              'enter it below with a new password.',
          )
          setStep('reset-code')
        } catch (err) {
          // Stay on reset-code when rate-limited so the error remains visible
          // and the user can still enter a code from a prior email.
          if (isLimitExceededError(err)) {
            startResetCooldown()
            setError(authErrorMessage(err, 'Could not start password reset'))
            setStep('reset-code')
            return
          }
          setError(authErrorMessage(err, 'Could not start password reset'))
        }
        return
      }
      // Any other challenge (MFA, TOTP setup, etc.) is not supported in-app yet —
      // surface a clear message instead of silently bouncing to the start.
      setError(
        'Your account needs an extra verification step we cannot complete here. ' +
          `Contact Ascend Health at ${DEVELOPER_CONTACT_EMAIL} for help.`,
      )
    } catch (err) {
      setError(authErrorMessage(err, 'Sign in failed'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleTotpSubmit = async (code: string) => {
    resetFeedback()
    setSubmitting(true)
    try {
      const result = await confirmTotpCode(code)
      if (result.isSignedIn) {
        navigate('/messages/direct', { replace: true })
        return
      }
      setError(
        'Code accepted, but another verification step is required. ' +
          `Contact Ascend Health at ${DEVELOPER_CONTACT_EMAIL} for help.`,
      )
    } catch (err) {
      setError(totpChallengeErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  const restartSignIn = () => {
    resetFeedback()
    setPassword('')
    setTotpCode('')
    setTotpSetup(null)
    setStep('password')
  }

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    resetFeedback()
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      const result = await submitNewPasswordChallenge(newPassword)
      if (result.isSignedIn) {
        // Invite complete: emailed temp password + new password were both just
        // proven — don't immediately soft-lock the first session for web MFA.
        satisfyWebMfaFromEmailedSecret()
        navigate('/messages/direct', { replace: true })
        return
      }
      // Unexpected additional challenge after setting the password.
      setError(
        'Password set, but another verification step is required. ' +
          `Contact Ascend Health at ${DEVELOPER_CONTACT_EMAIL} for help.`,
      )
    } catch (err) {
      setError(authErrorMessage(err, 'Could not set your new password'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleForgotPassword = async () => {
    // Keep prior error visible if we bounce for validation — only clear on a real attempt.
    if (!emailValid) {
      setMessage(null)
      setError('Enter a valid email address first')
      setStep('email')
      return
    }
    if (resetInFlight.current || submitting) return
    if (resetCooldownRemainingSec > 0) {
      setMessage(null)
      setError(
        `Please wait ${resetCooldownRemainingSec}s before requesting another reset code. ` +
          'Cognito limits how often codes can be sent.',
      )
      // Stay on reset-code if we were already there; otherwise password.
      if (step !== 'reset-code') setStep('password')
      return
    }

    setMessage(null)
    setError(null)
    setSubmitting(true)
    resetInFlight.current = true
    try {
      await requestPasswordReset(trimmedEmail)
      startResetCooldown()
      setMessage(
        `If an account exists for ${trimmedEmail}, a reset code was sent. Check your inbox and spam folder.`,
      )
      setStep('reset-code')
    } catch (err) {
      // Always start cooldown on rate-limit so Resend cannot hammer Cognito.
      if (isLimitExceededError(err)) startResetCooldown()
      if (isForceChangePasswordResetError(err)) {
        setError(
          "You haven't set a password yet. Go back and sign in with the temporary " +
            'password from your invite email — you\'ll be prompted to choose a new one.',
        )
        setStep('password')
        return
      }
      // Keep the user on the current step (or reset-code) so the error stays visible
      // instead of looking like a silent bounce to sign-in.
      setError(authErrorMessage(err, 'Could not start password reset'))
      if (step === 'reset-code' || isLimitExceededError(err)) {
        setStep('reset-code')
      }
    } finally {
      resetInFlight.current = false
      setSubmitting(false)
    }
  }

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault()
    resetFeedback()
    if (!resetCode.trim()) {
      setError('Enter the verification code from your email')
      return
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      await completePasswordReset(trimmedEmail, resetCode, newPassword)
      // Emailed reset code + new password were both just proven in this
      // browser — the upcoming sign-in must not instantly re-prompt web MFA.
      satisfyWebMfaFromEmailedSecret()
      setMessage('Password updated. Sign in with your new password.')
      setPassword('')
      setResetCode('')
      setNewPassword('')
      setConfirmNewPassword('')
      setStep('password')
    } catch (err) {
      // Stay on reset-code with the error — do not bounce to password/email.
      setError(authErrorMessage(err, 'Could not reset password'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClearSession = async () => {
    resetFeedback()
    setSubmitting(true)
    try {
      await clearAuthSession()
      setMessage('Session cleared. Try signing in again.')
    } catch (err) {
      setError(authErrorMessage(err, 'Could not clear session'))
    } finally {
      setSubmitting(false)
    }
  }

  const stepTitle = useMemo(() => {
    switch (step) {
      case 'email':
        return 'Sign in to your account'
      case 'password':
        return 'Enter your password'
      case 'new-password':
        return 'Set your new password'
      case 'forgot-username':
        return 'Forgot username?'
      case 'reset-code':
        return 'Reset your password'
      case 'totp-code':
        return 'Enter your authenticator code'
      case 'totp-setup':
        return 'Set up your authenticator app'
      default:
        return 'Sign in'
    }
  }, [step])

  if (!isConfigured) {
    return (
      <div data-status-scrim="transparent" className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 to-slate-100 p-6 pt-[max(1.5rem,var(--safe-top))] pb-[max(1.5rem,var(--safe-bottom))]">
        <div className="max-w-md rounded-2xl bg-white p-8 shadow-lg">
          <h1 className="text-xl font-bold text-slate-900">Cognito not configured</h1>
          <p className="mt-2 text-sm text-slate-600">
            Copy your pool settings into <code className="rounded bg-slate-100 px-1">frontend/.env.local</code>{' '}
            (see <code className="rounded bg-slate-100 px-1">frontend/.env.example</code>).
          </p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div data-status-scrim="transparent" className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 to-slate-100 p-6 pt-[max(1.5rem,var(--safe-top))] pb-[max(1.5rem,var(--safe-bottom))]">
        <p className="text-sm text-slate-600">Checking session…</p>
      </div>
    )
  }

  return (
    <div data-status-scrim="transparent" className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-slate-50 to-slate-100 p-6 pt-[max(1.5rem,var(--safe-top))] pb-[max(1.5rem,var(--safe-bottom))]">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl ring-1 ring-slate-200/60">
        <div className="mb-8 text-center">
          <img
            src={BRAND_LOGO_URL}
            alt="Ascend"
            className="mx-auto h-16 w-16 rounded-2xl shadow-sm object-contain"
          />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Welcome to Ascend Messenger</h1>
          <p className="mt-1 text-sm text-slate-500">{stepTitle}</p>
        </div>

        {message && (
          <div
            className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
            role="status"
            aria-live="polite"
          >
            {message}
          </div>
        )}

        {error && (
          <div
            className="mb-4 space-y-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
            aria-live="assertive"
          >
            <p>{error}</p>
            {/already a signed in user/i.test(error) && (
              <button
                type="button"
                onClick={() => void handleClearSession()}
                className="text-xs font-semibold text-red-800 underline"
              >
                Clear session and try again
              </button>
            )}
          </div>
        )}

        {(step === 'email' || step === 'new-password') && <MobileAppHint />}

        {step === 'email' && (
          <form onSubmit={handleContinueToPassword} className="space-y-4">
            <label className="block text-sm font-medium text-slate-700">
              Email
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  resetFeedback()
                }}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none ring-brand-500 focus:ring-2"
                aria-invalid={email.length > 0 && !emailValid}
              />
            </label>
            <button
              type="submit"
              disabled={!emailValid || submitting}
              className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue
            </button>
            <button
              type="button"
              onClick={() => {
                resetFeedback()
                setStep('forgot-username')
              }}
              className="w-full text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Forgot username?
            </button>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
              <span className="block text-xs font-medium uppercase tracking-wide text-slate-500">
                Signing in as
              </span>
              <span className="mt-0.5 block truncate font-medium">{trimmedEmail}</span>
            </div>
            <PasswordField
              label="Password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                resetFeedback()
              }}
            />
            <button
              type="submit"
              disabled={submitting || !password}
              className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  resetFeedback()
                  setStep('email')
                }}
                className="text-sm font-medium text-slate-600 hover:text-slate-800"
              >
                ← Change email
              </button>
              <button
                type="button"
                disabled={submitting || resetCooldownRemainingSec > 0}
                onClick={() => void handleForgotPassword()}
                className="text-sm font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
              >
                {resetCooldownRemainingSec > 0
                  ? `Forgot password? (${resetCooldownRemainingSec}s)`
                  : 'Forgot password?'}
              </button>
            </div>
          </form>
        )}

        {step === 'totp-code' && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleTotpSubmit(totpCode)
            }}
            className="space-y-4"
          >
            <p className="text-sm text-slate-600">
              Open your authenticator app and enter the current six-digit code for{' '}
              <strong>{trimmedEmail}</strong>. Codes change every 30 seconds.
            </p>
            <label htmlFor="totp-code" className="block text-sm font-medium text-slate-700">
              Six-digit code
            </label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={7}
              value={totpCode}
              onChange={(e) => {
                setTotpCode(e.target.value)
                resetFeedback()
              }}
              aria-invalid={Boolean(error)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-center font-mono text-lg tracking-[0.4em] outline-none ring-brand-500 focus:ring-2"
            />
            <button
              type="submit"
              disabled={submitting || !isCompleteTotpCode(totpCode)}
              className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <p className="text-xs text-slate-500">
              Lost the phone with your authenticator app? An Ascend Health owner can remove it from
              your account so you can sign in and set it up again — email{' '}
              <a
                href={`mailto:${DEVELOPER_CONTACT_EMAIL}?subject=${encodeURIComponent('Lost authenticator app — Ascend Messenger')}`}
                className="font-semibold text-brand-700 underline-offset-2 hover:underline"
              >
                {DEVELOPER_CONTACT_EMAIL}
              </a>
              .
            </p>
            <button
              type="button"
              onClick={restartSignIn}
              className="w-full text-sm font-medium text-slate-600 hover:text-slate-800"
            >
              ← Back to sign in
            </button>
          </form>
        )}

        {step === 'totp-setup' &&
          (totpSetup ? (
            <TotpEnrolmentPanel
              secret={totpSetup.secret}
              otpauthUri={totpSetup.otpauthUri}
              submitting={submitting}
              // Errors render in the page-level alert above, not twice.
              error={null}
              submitLabel="Confirm and sign in"
              onSubmit={(code) => void handleTotpSubmit(code)}
              onCancel={restartSignIn}
              cancelLabel="Back to sign in"
            />
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                We could not start authenticator setup. Enter your password again to retry.
              </p>
              <button
                type="button"
                onClick={restartSignIn}
                className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
              >
                Back to sign in
              </button>
            </div>
          ))}

        {step === 'new-password' && (
          <form onSubmit={handleSetNewPassword} className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
              <span className="block text-xs font-medium uppercase tracking-wide text-slate-500">
                Setting password for
              </span>
              <span className="mt-0.5 block truncate font-medium">{trimmedEmail}</span>
            </div>
            <PasswordField
              label="New password"
              autoComplete="new-password"
              autoFocus
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                resetFeedback()
              }}
              aria-describedby="new-password-hint"
            />
            <PasswordField
              label="Confirm new password"
              autoComplete="new-password"
              value={confirmNewPassword}
              onChange={(e) => {
                setConfirmNewPassword(e.target.value)
                resetFeedback()
              }}
            />
            <p id="new-password-hint" className="text-xs text-slate-500">
              Use at least 8 characters. Choose something you haven't used here before.
            </p>
            <button
              type="submit"
              disabled={submitting || !newPassword || !confirmNewPassword}
              className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Saving password…' : 'Set password and sign in'}
            </button>
            <button
              type="button"
              onClick={() => {
                resetFeedback()
                setPassword('')
                setNewPassword('')
                setConfirmNewPassword('')
                setStep('email')
              }}
              className="w-full text-sm font-medium text-slate-600 hover:text-slate-800"
            >
              ← Start over
            </button>
          </form>
        )}

        {step === 'forgot-username' && (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-slate-600">
              Usernames are managed by Ascend Health. If you do not know the email address linked to
              your account, contact Ascend Health directly and our team will help you.
            </p>
            <a
              href={`mailto:${DEVELOPER_CONTACT_EMAIL}?subject=${encodeURIComponent('Forgot username — Ascend Messenger')}`}
              className="block rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-center text-sm font-semibold text-brand-700 hover:bg-brand-100"
            >
              Email {DEVELOPER_CONTACT_EMAIL}
            </a>
            <button
              type="button"
              onClick={() => {
                resetFeedback()
                setStep(emailValid ? 'password' : 'email')
              }}
              className="w-full text-sm font-medium text-slate-600 hover:text-slate-800"
            >
              ← Back to sign in
            </button>
          </div>
        )}

        {step === 'reset-code' && (
          <form onSubmit={handleConfirmReset} className="space-y-4">
            <p className="text-sm text-slate-600">
              Enter the verification code sent to <strong>{trimmedEmail}</strong>. Check your spam
              folder if you do not see it within a few minutes.
            </p>
            <label className="block text-sm font-medium text-slate-700">
              Verification code
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={resetCode}
                onChange={(e) => {
                  setResetCode(e.target.value)
                  // Do not clear a rate-limit error while typing the code —
                  // only clear when the value actually changes meaningfully via submit.
                  setMessage(null)
                }}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none ring-brand-500 focus:ring-2"
              />
            </label>
            <PasswordField
              label="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                setMessage(null)
              }}
            />
            <PasswordField
              label="Confirm new password"
              autoComplete="new-password"
              value={confirmNewPassword}
              onChange={(e) => {
                setConfirmNewPassword(e.target.value)
                setMessage(null)
              }}
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? 'Updating password…' : 'Update password'}
            </button>
            <button
              type="button"
              disabled={submitting || resetCooldownRemainingSec > 0}
              onClick={() => void handleForgotPassword()}
              className="w-full text-sm font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
            >
              {resetCooldownRemainingSec > 0
                ? `Resend code (${resetCooldownRemainingSec}s)`
                : 'Resend code'}
            </button>
            <button
              type="button"
              onClick={() => {
                // Preserve rate-limit / reset errors when going back so the
                // user still sees why reset failed.
                setMessage(null)
                setStep('password')
              }}
              className="w-full text-sm font-medium text-slate-600 hover:text-slate-800"
            >
              ← Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
